import { Listing, Unit } from '../types'
import {
  HONESTY_SENTENCE,
  PRIORITY_CARDS,
  PriorityCard,
  PriorityGroup,
  priorityCardBySlug,
} from './priority_cards'

export const SITE_ORIGIN = 'https://holic.net'
export const HUB_PATH = '/cards/index.html'
export const HUB_TITLE = '注目カードの販売一覧 | HOLIC'
export const HUB_DESCRIPTION = '旧裏・初版の定番、プロモ、相場で検索されるカードを、カタログの事実と販売ページへのリンクだけで案内します。ブラッキーのSARはカタログ未収録のため、載せていません。'
export const HUB_INTRO = '買取・販売・相場で名前が検索されるカードのうち、カタログで中身を特定できるものだけを掲載しています。各ページの文章は、そのカードの収録・イラストレーター・ワザから書いています。同じ文面の量産ページはありません。出品が無いカードに、価格は書いていません。'

export type MarketView =
  | Readonly<{ kind: 'unknown' }>
  | Readonly<{ kind: 'none' }>
  | Readonly<{ kind: 'asks', askCount: number, minPriceJpy: number, maxPriceJpy: number }>

export type AttributeRow = Readonly<{
  label: string
  value: string
}>

export type AskLink = Readonly<{
  href: string
  priceJpy: number
  condition: number
}>

const GROUP_ORDER: readonly PriorityGroup[] = ['classic', 'promo', 'market']

export const GROUP_COPY: Readonly<Record<PriorityGroup, Readonly<{ heading: string, lead: string }>>> = {
  classic: {
    heading: '旧裏・初版と拡張の定番',
    lead: '第1弾の初版・旧裏面や、化石の秘密、ロケット団、neoのレアなど、鑑定でも名前が出るカードです。',
  },
  promo: {
    heading: 'プロモの販売',
    lead: 'キャンペーンや付録のプロモは、拡張パックの同名カードと別レコードです。',
  },
  market: {
    heading: '相場で探されやすいカード',
    lead: 'ひかるリザードン、neoのブラッキー、イーブイヒーローズのブラッキーVMAX、仰天のボルテッカーのピカチュウVMAXです。SARのブラッキーはカタログにありません。',
  },
}

export const cardPublicPath = (slug: string): string => `/cards/${slug}/index.html`

export const cardCanonicalUrl = (slug: string): string => `${SITE_ORIGIN}${cardPublicPath(slug)}`

export const itemPath = (card: PriorityCard): string => {
  return `/categories/${card.categoryId}/items/${card.itemId}`
}

export const categoryPath = (id: number): string => `/categories/${id}`

export const cardTitle = (card: PriorityCard, includeMarket: boolean): string => {
  if (!includeMarket) return card.title

  return card.title.replace('の販売 | HOLIC', 'の販売・相場 | HOLIC')
}

export const formatReleaseDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-')
  return `${year}年${Number(month)}月${Number(day)}日`
}

export const formatYen = (priceJpy: number): string => {
  return `${priceJpy.toLocaleString('ja-JP')}円`
}

const lowestUnsoldHolicAsk = (unit: Unit): Listing | undefined => {
  let best: Listing | undefined

  for (const listing of unit.listings ?? []) {
    if (listing.platform !== 'holic' || listing.soldAt) continue

    const price = Math.round(listing.priceJpy)
    if (price <= 0) continue
    if (!best || price < Math.round(best.priceJpy)) best = listing
  }

  return best
}

const activeAskPrices = (units: readonly Unit[] | undefined): number[] => {
  const prices: number[] = []

  for (const unit of units ?? []) {
    const listing = lowestUnsoldHolicAsk(unit)
    if (!listing) continue

    prices.push(Math.round(listing.priceJpy))
  }

  return prices
}

export const marketViewFromUnits = (
  units: readonly Unit[] | undefined,
  listingsLoading: boolean,
): MarketView => {
  if (listingsLoading || units === undefined) return { kind: 'unknown' }

  const prices = activeAskPrices(units)
  if (prices.length === 0) return { kind: 'none' }

  return {
    kind: 'asks',
    askCount: prices.length,
    minPriceJpy: Math.min(...prices),
    maxPriceJpy: Math.max(...prices),
  }
}

export const askLinksFromUnits = (units: readonly Unit[] | undefined): AskLink[] => {
  const links: AskLink[] = []

  for (const unit of units ?? []) {
    const listing = lowestUnsoldHolicAsk(unit)
    if (!listing || !unit.userId || !unit.id) continue

    const price = Math.round(listing.priceJpy)

    links.push({
      href: `/u/${unit.userId}/units/${unit.id}`,
      priceJpy: price,
      condition: unit.condition,
    })
  }

  return links
}

export const marketParagraph = (view: MarketView): string => {
  if (view.kind === 'unknown') {
    return '販売中の価格は出品のたびに変わります。最新の件数と販売価格は、販売一覧で確認できます。'
  }

  if (view.kind === 'none') {
    return 'いま販売中の出品はありません。出品が付くと、販売一覧に価格が出ます。'
  }

  if (view.askCount === 1) {
    return `販売中の出品は1件で、販売価格は${formatYen(view.minPriceJpy)}です。これは店舗が買い取る金額ではなく、出品者が付けた販売価格です。`
  }

  return `販売中の出品は${view.askCount}件です。いちばん低い販売価格は${formatYen(view.minPriceJpy)}、高いものは${formatYen(view.maxPriceJpy)}です。これは店舗が買い取る金額ではなく、出品者が付けた販売価格です。`
}

export const metaDescription = (card: PriorityCard, view: MarketView): string => {
  if (view.kind !== 'asks') return card.description

  const extra = view.askCount === 1
    ? `販売中1件、${formatYen(view.minPriceJpy)}。`
    : `販売中${view.askCount}件、最安${formatYen(view.minPriceJpy)}。`
  const combined = `${card.description}${extra}`

  return combined.length <= 150 ? combined : card.description
}

const attackLine = (attack: PriorityCard['attacks'][number]): string => {
  const damage = attack.damage ? `（ダメージ${attack.damage}）` : ''
  const text = attack.text ? `${attack.text}` : ''

  return [attack.name + damage, text].filter((part) => part.length > 0).join('。')
}

export const attributeRows = (card: PriorityCard): AttributeRow[] => {
  const rows: AttributeRow[] = []

  if (card.artist) rows.push({ label: 'イラストレーター', value: card.artist })
  if (card.hp) rows.push({ label: 'HP', value: card.hp })
  if (card.level) rows.push({ label: 'レベル', value: card.level })
  if (card.typesJa.length > 0) rows.push({ label: 'タイプ', value: card.typesJa.join('・') })
  if (card.stageJa) rows.push({ label: '進化', value: card.stageJa })
  if (card.rarityJa) rows.push({ label: 'レアリティ', value: card.rarityJa })
  if (card.releaseDate) rows.push({ label: '発売日', value: formatReleaseDate(card.releaseDate) })
  if (card.setReleaseDate) rows.push({ label: '収録パックの発売日', value: formatReleaseDate(card.setReleaseDate) })

  card.attacks.forEach((attack) => {
    rows.push({ label: 'ワザ', value: attackLine(attack) })
  })

  card.abilities.forEach((ability) => {
    rows.push({ label: '特性', value: `${ability.name}。${ability.text}` })
  })

  if (card.flavorText) rows.push({ label: 'フレーバー', value: card.flavorText })

  rows.push({ label: '英語名', value: card.nameEn })

  return rows
}

type JsonLd = Readonly<Record<string, unknown>>

export const jsonLd = (card: PriorityCard, view: MarketView): JsonLd => {
  const crumbs: JsonLd[] = [
    { '@type': 'ListItem', position: 1, name: 'HOLIC', item: `${SITE_ORIGIN}/` },
  ]
  let position = 2

  if (card.series) {
    crumbs.push({
      '@type': 'ListItem',
      position,
      name: card.series.nameJa,
      item: `${SITE_ORIGIN}${categoryPath(card.series.id)}`,
    })
    position += 1
  }

  crumbs.push({
    '@type': 'ListItem',
    position,
    name: card.set.nameJa,
    item: `${SITE_ORIGIN}${categoryPath(card.set.id)}`,
  })
  position += 1

  crumbs.push({
    '@type': 'ListItem',
    position,
    name: card.h1,
    item: cardCanonicalUrl(card.slug),
  })

  const product: Record<string, unknown> = {
    '@type': 'Product',
    name: card.h1,
    description: metaDescription(card, view),
    category: card.set.nameJa,
    sku: String(card.itemId),
    url: cardCanonicalUrl(card.slug),
    brand: { '@type': 'Brand', name: 'ポケモンカードゲーム' },
    additionalProperty: attributeRows(card).map((row) => ({
      '@type': 'PropertyValue',
      name: row.label,
      value: row.value,
    })),
  }

  if (view.kind === 'asks') {
    product.offers = {
      '@type': 'AggregateOffer',
      priceCurrency: 'JPY',
      lowPrice: String(view.minPriceJpy),
      highPrice: String(view.maxPriceJpy),
      offerCount: view.askCount,
      availability: 'https://schema.org/InStock',
      url: `${SITE_ORIGIN}${itemPath(card)}`,
    }
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'BreadcrumbList', itemListElement: crumbs },
      product,
    ],
  }
}

const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const jsonForScript = (value: unknown): string => {
  return JSON.stringify(value).replace(/</g, '\\u003c')
}

const linkList = (links: readonly Readonly<{ href: string, label: string }>[]): string => {
  return `<ul>${links.map((link) => `<li><a href="${escapeHtml(link.href)}">${escapeHtml(link.label)}</a></li>`).join('')}</ul>`
}

const pageShell = (opts: Readonly<{
  title: string
  description: string
  canonical: string
  json: unknown
  body: string
}>): string => {
  return `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(opts.title)}</title>
<meta name="description" content="${escapeHtml(opts.description)}">
<link rel="canonical" href="${escapeHtml(opts.canonical)}">
<meta property="og:title" content="${escapeHtml(opts.title)}">
<meta property="og:description" content="${escapeHtml(opts.description)}">
<meta property="og:type" content="website">
<meta property="og:url" content="${escapeHtml(opts.canonical)}">
<meta property="og:locale" content="ja_JP">
<script type="application/ld+json">${jsonForScript(opts.json)}</script>
<style>
body{font-family:sans-serif;line-height:1.7;margin:0;color:#1c1917;background:#fff}
main{max-width:40rem;margin:0 auto;padding:1.5rem 1rem 3rem}
a{color:#9a3412}
h1{font-size:1.6rem;line-height:1.3}
dt{font-weight:700;margin-top:.6rem}
dd{margin:0}
nav ul,main ul{padding-left:1.2rem}
.honesty{background:#fff7ed;padding:.8rem 1rem;border-radius:.5rem}
</style>
</head>
<body>
<main>
${opts.body}
</main>
</body>
</html>
`
}

const crossLinks = (card: PriorityCard): string => {
  const links: Array<{ href: string, label: string }> = [
    { href: categoryPath(card.set.id), label: `${card.set.nameJa}のカード一覧` },
    { href: itemPath(card), label: `${card.h1}の販売一覧` },
    { href: HUB_PATH, label: '注目カードの一覧' },
  ]

  if (card.printing && card.printing.id !== card.set.id) {
    links.unshift({ href: categoryPath(card.printing.id), label: card.printing.nameJa })
  }

  if (card.series && card.series.id !== card.set.id) {
    links.unshift({ href: categoryPath(card.series.id), label: card.series.nameJa })
  }

  for (const extra of card.alsoIn ?? []) {
    links.push({ href: categoryPath(extra.id), label: extra.nameJa })
  }

  for (const slug of card.relatedSlugs) {
    const related = priorityCardBySlug(slug)
    if (!related) continue
    links.push({ href: cardPublicPath(slug), label: related.h1 })
  }

  return linkList(links)
}

export const renderCardLandingDocument = (card: PriorityCard): string => {
  const view: MarketView = { kind: 'unknown' }
  const rows = attributeRows(card).map((row) => {
    return `<dt>${escapeHtml(row.label)}</dt><dd>${escapeHtml(row.value)}</dd>`
  }).join('')

  const body = `<nav><a href="${HUB_PATH}">注目カード</a></nav>
<h1>${escapeHtml(card.h1)}</h1>
<p>${escapeHtml(card.intro)}</p>
<p class="honesty">${escapeHtml(HONESTY_SENTENCE)}</p>
<h2>カタログ</h2>
<dl>${rows}</dl>
<h2>販売と相場</h2>
<p>${escapeHtml(marketParagraph(view))}</p>
<p>販売一覧の各出品から、出品者のユニット（/u/出品者/units/番号）へ進めます。ユニットの番号は出品があるときだけ付きます。</p>
<h2>セット・カード・関連</h2>
${crossLinks(card)}
`

  return pageShell({
    title: cardTitle(card, false),
    description: card.description,
    canonical: cardCanonicalUrl(card.slug),
    json: jsonLd(card, view),
    body,
  })
}

export const renderHubDocument = (): string => {
  const sections = GROUP_ORDER.map((group) => {
    const copy = GROUP_COPY[group]
    const cards = PRIORITY_CARDS.filter((card) => card.group === group)
    const items = cards.map((card) => {
      return `<li><a href="${cardPublicPath(card.slug)}">${escapeHtml(card.h1)}</a> — <a href="${itemPath(card)}">販売一覧</a> / <a href="${categoryPath(card.set.id)}">${escapeHtml(card.set.nameJa)}</a></li>`
    }).join('')

    return `<h2>${escapeHtml(copy.heading)}</h2><p>${escapeHtml(copy.lead)}</p><ul>${items}</ul>`
  }).join('')

  const body = `<h1>注目カードの販売</h1>
<p>${escapeHtml(HUB_INTRO)}</p>
<p class="honesty">${escapeHtml(HONESTY_SENTENCE)}</p>
${sections}
<p><a href="/">HOLICのトップ</a></p>
`

  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: '注目カードの販売',
    itemListElement: PRIORITY_CARDS.map((card, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: card.h1,
      url: cardCanonicalUrl(card.slug),
    })),
  }

  return pageShell({
    title: HUB_TITLE,
    description: HUB_DESCRIPTION,
    canonical: `${SITE_ORIGIN}${HUB_PATH}`,
    json: itemList,
    body,
  })
}

export const renderCardSitemap = (): string => {
  const urls = [HUB_PATH, ...PRIORITY_CARDS.map((card) => cardPublicPath(card.slug))]
  const body = urls.map((path) => {
    return `  <url><loc>${SITE_ORIGIN}${path}</loc></url>`
  }).join('\n')

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${body}
</urlset>
`
}
